import { Id } from '@/infra/id'
import { collection } from '@/infra/collection'
import { onRpcNotFoundReturnNull } from '@/utils/grpc-util'
import { type JobsApi } from '@/features/jobs/api/job-api'
import {
  CreateTaxRefundMethodRequest,
  GetBusinessTaxRefundMethodForJobRequest,
  GetPersonalTaxRefundMethodForJobRequest,
  UpdateTaxRefundMethodRequest,
} from '@taxfyle/api-internal/internal/tax_refund_methods_pb'
import type { TaxRefundDeliveryMethod } from './TaxRefundDeliveryMethod'
import {
  TaxRefundMethodVersion,
  TaxRefundMethodVersionId,
  type TaxRefundType,
} from './TaxRefundMethodVersion'
import {
  mapTaxRefundDeliveryMethodToProto,
  mapTaxRefundMethodProtoToProps,
  mapTaxRefundTypeToProto,
} from './tax-refund-method-mappers'

/**
 * Manages the tax refund methods captured for jobs.
 */
export class TaxRefundMethodStore {
  readonly taxRefundMethodVersions = collection({
    create: () => new TaxRefundMethodVersion(),
    getDataId: (d) => TaxRefundMethodVersionId.from(d.id, d.version),
    getModelId: (m) => TaxRefundMethodVersionId.from(m.id, m.version),
  })

  constructor(private readonly _api: JobsApi) {}

  /**
   * Gets a tax refund method version.
   */
  getTaxRefundMethod(id: Id, version: number) {
    return this.taxRefundMethodVersions.get(
      TaxRefundMethodVersionId.from(id, version),
    )
  }

  /**
   * Fetches the business tax refund method provided to a job.
   */
  async fetchBusinessTaxRefundMethodForJob(
    jobId: Id,
    signal?: AbortSignal,
  ): Promise<TaxRefundMethodVersion | null> {
    const request = new GetBusinessTaxRefundMethodForJobRequest().setJobId(
      Id.assert(jobId),
    )
    const response = await this._api.taxRefundMethods
      .getBusinessTaxRefundMethodForJob(request)
      .catch(onRpcNotFoundReturnNull)
    signal?.throwIfAborted()
    if (!response) {
      return null
    }

    return this.taxRefundMethodVersions.set(
      mapTaxRefundMethodProtoToProps(response),
    )
  }

  /**
   * Fetches the personal tax refund method provided to a job.
   */
  async fetchPersonalTaxRefundMethodForJob(
    jobId: Id,
    signal?: AbortSignal,
  ): Promise<TaxRefundMethodVersion | null> {
    const request = new GetPersonalTaxRefundMethodForJobRequest().setJobId(
      Id.assert(jobId),
    )
    const response = await this._api.taxRefundMethods
      .getPersonalTaxRefundMethodForJob(request)
      .catch(onRpcNotFoundReturnNull)
    signal?.throwIfAborted()
    if (!response) {
      return null
    }

    return this.taxRefundMethodVersions.set(
      mapTaxRefundMethodProtoToProps(response),
    )
  }

  /**
   * Creates a tax refund method.
   */
  async createTaxRefundMethod(
    input: CreateTaxRefundMethodOptions,
  ): Promise<TaxRefundMethodVersion> {
    const request = new CreateTaxRefundMethodRequest()
      .setWorkspaceId(Id.assert(input.workspaceId))
      .setOwnerUserId(Id.assert(input.ownerUserId))
      .setRefundType(mapTaxRefundTypeToProto(input.refundType))
      .setDeliveryMethod(
        mapTaxRefundDeliveryMethodToProto(input.deliveryMethod),
      )

    const response =
      await this._api.taxRefundMethods.createTaxRefundMethod(request)

    return this.taxRefundMethodVersions.set({
      id: Id(response.getId()),
      version: response.getVersion(),
      refundType: input.refundType,
      deliveryMethod: input.deliveryMethod,
    })
  }

  /**
   * Saves a new version of a tax refund method.
   */
  async updateTaxRefundMethod(
    input: UpdateTaxRefundMethodOptions,
  ): Promise<TaxRefundMethodVersion> {
    const request = new UpdateTaxRefundMethodRequest()
      .setId(Id.assert(input.id))
      .setDeliveryMethod(
        mapTaxRefundDeliveryMethodToProto(input.deliveryMethod),
      )

    const response =
      await this._api.taxRefundMethods.updateTaxRefundMethod(request)

    return this.taxRefundMethodVersions.set({
      id: Id(response.getId()),
      version: response.getVersion(),
      refundType: input.refundType,
      deliveryMethod: input.deliveryMethod,
    })
  }
}

/**
 * Options for creating a tax refund method.
 */
export interface CreateTaxRefundMethodOptions {
  workspaceId: Id
  ownerUserId: Id
  refundType: TaxRefundType
  deliveryMethod: TaxRefundDeliveryMethod
}

/**
 * Options for saving a new version of a tax refund method.
 */
export interface UpdateTaxRefundMethodOptions {
  id: Id
  refundType: TaxRefundType
  deliveryMethod: TaxRefundDeliveryMethod
}
